import { useQuery } from "@tanstack/react-query";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import CloseIcon from "../../icons/iconClose.comp";
import { RefObject, useRef } from "react";
import SolidWeakBtn from "../form/solidWeakBtn.comp";
import membershipCore from "../../core/membership.core";
import localeCore from "../../core/locale.core";
import { Membership } from "../../types/membership";
import { useRouter } from "next/router";

export default function MultipleMembershipsSelector({
  methods,
  fieldName,
  buttonText,
  btnId,
}: {
  methods: UseFormReturn<any, any>;
  fieldName: string;
  buttonText?: string;
  btnId?: string;
}) {
  const { t, msg } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  function deleteMembership(membershipId: string) {
    const membershipIds = methods.getValues(fieldName) || [];

    const set = new Set(membershipIds);
    set.delete(membershipId);
    methods.setValue(fieldName, Array.from(set));
  }

  return (
    <div className="flex flex-col gap-2">
      <AttachBadges methods={methods} fieldName={fieldName} handleDelete={deleteMembership} />
      <button
        type="button"
        id={btnId}
        className="bg-her-blue px-4 py-2 rounded-md text-white w-fit"
        onClick={() => dialogRef.current?.showModal()}
      >
        {buttonText ?? t(msg.membership.add)}
      </button>
      <MembershipSelectorDialog dialogRef={dialogRef} methods={methods} fieldName={fieldName} />
    </div>
  );
}

const AttachBadges = ({
  methods,
  fieldName,
  handleDelete,
}: {
  methods: UseFormReturn<any, any>;
  fieldName: string;
  handleDelete: (membershipId: string) => void;
}) => {
  const membershipIds = useWatch({ control: methods.control, name: fieldName }) || [];

  if (membershipIds.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {membershipIds.map((membershipId: string) => (
        <MembershipBadge key={membershipId} membershipId={membershipId} handleDelete={handleDelete} />
      ))}
    </div>
  );
};

function MembershipBadge({ membershipId, handleDelete }: { membershipId: string; handleDelete: (id: string) => void }) {
  const { t } = useTranslation();
  const {
    data: membership,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["membership", membershipId],
    queryFn: () => membershipCore.getOne(membershipId),
  });
  const router = useRouter();

  if (isPending) return <div className="bg-admin-bg-blue px-2 py-1 rounded text-xs">Loading...</div>;
  if (isError || !membership) return null;

  const locale = router.locale || "en";
  const name = localeCore.getLocaleValue(membership.name, locale) || membership.name.en || "";

  return (
    <div className="bg-admin-bg-blue px-2 py-1 rounded text-xs flex items-center gap-1">
      <span>{name}</span>
      <button type="button" onClick={() => handleDelete(membershipId)} className="hover:text-red-400">
        <CloseIcon />
      </button>
    </div>
  );
}

interface MembershipSelectorDialogProps {
  dialogRef: RefObject<HTMLDialogElement>;
  methods: UseFormReturn<any, any>;
  fieldName: string;
}

function MembershipSelectorDialog({ dialogRef, methods, fieldName }: MembershipSelectorDialogProps) {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const locale = router.locale || "en";
  const {
    data: memberships,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["memberships"],
    queryFn: () => membershipCore.loadMemberships(),
  });

  const selectedMembershipIds = useWatch({ control: methods.control, name: fieldName }) || [];

  function addMembership(membershipId: string) {
    const membershipIds = methods.getValues(fieldName) || [];

    const set = new Set(membershipIds);
    set.add(membershipId);
    methods.setValue(fieldName, Array.from(set));
  }

  if (isPending) {
    return (
      <dialog
        ref={dialogRef}
        className="dark:bg-her-blackOlive dark:text-white p-10 rounded-lg w-[80vw] max-w-[700px] fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <div>Loading...</div>
      </dialog>
    );
  }

  if (isError || !memberships) {
    return (
      <dialog
        ref={dialogRef}
        className="dark:bg-her-blackOlive dark:text-white p-10 rounded-lg w-[80vw] max-w-[700px] fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <div>Error loading memberships</div>
      </dialog>
    );
  }

  const availableMemberships = memberships.filter((m) => !selectedMembershipIds.includes(m.id));

  return (
    <dialog ref={dialogRef} className="p-4 dark:bg-her-backblue w-[500px] dark:text-white">
      <div className="flex flex-col gap-2">
        <h2 className="text-xl mb-4">{t(msg.membership.selectMembership)}</h2>
        <div id="select-memberships-list" className="max-h-[400px] overflow-y-auto space-y-2">
          {availableMemberships.length === 0 ? (
            <p className="text-gray-400">{t(msg.membership.noMembershipsAvailable)}</p>
          ) : (
            availableMemberships.map((membership) => {
              const name = localeCore.getLocaleValue(membership.name, locale) || membership.name.en || "";
              return (
                <button
                  key={membership.id}
                  type="button"
                  onClick={() => addMembership(membership.id)}
                  className="w-full text-left px-4 py-2 bg-admin-bg-blue hover:bg-admin-bg-blue-dark rounded"
                >
                  {name}
                </button>
              );
            })
          )}
        </div>
        <AttachBadges
          methods={methods}
          fieldName={fieldName}
          handleDelete={(id) => {
            const membershipIds = methods.getValues(fieldName) || [];
            const set = new Set(membershipIds);
            set.delete(id);
            methods.setValue(fieldName, Array.from(set));
          }}
        />
      </div>
      <div className="flex gap-4 mt-6">
        <SolidWeakBtn id="close-membership-selector" type="button" onClick={() => dialogRef.current?.close()}>
          {t(msg.form.close)}
        </SolidWeakBtn>
      </div>
    </dialog>
  );
}
